import { kml } from '@tmcw/togeojson';
import { extractCoordinatesFromWKT } from '@app/utils/wkt-parser';

export const GEOMETRY_FILE_ACCEPT = '.kml,.geojson,.json,.xml';

type GeoJsonObject = {
  type?: string;
  coordinates?: unknown;
  geometry?: GeoJsonObject;
  geometries?: GeoJsonObject[];
  features?: Array<{ geometry?: GeoJsonObject }>;
};

export function assignmentWktToGeoJson(wkt: string | null | undefined): string | null {
  const polygons = extractCoordinatesFromWKT(wkt ?? '');
  if (!polygons.length) {
    return null;
  }
  const lngLat = polygons.map(rings => rings.map(ring => ring.map(([lat, lng]) => [lng, lat])));
  const geometry = lngLat.length === 1
    ? { type: 'Polygon', coordinates: lngLat[0] }
    : { type: 'MultiPolygon', coordinates: lngLat };
  return JSON.stringify(geometry);
}

export function assignmentGeoJsonToWkt(geoJson: string | null | undefined): string | null {
  const geometry = geometryFromUnknown(tryParseJson(geoJson));
  if (!geometry) {
    return null;
  }
  const polygons = polygonRingsFromGeometry(geometry);
  if (!polygons.length) {
    return null;
  }
  if (polygons.length === 1) {
    return `POLYGON ${formatWktPolygon(polygons[0])}`;
  }
  return `MULTIPOLYGON (${polygons.map(polygon => `(${formatWktPolygon(polygon)})`).join(', ')})`;
}

export function normalizeAssignmentGeoJson(geoJson: string | null | undefined): string | null {
  const geometry = geometryFromUnknown(tryParseJson(geoJson));
  if (!geometry) {
    return null;
  }
  return JSON.stringify(geometry);
}

export function parseAssignmentGeometryFile(file: File, text: string): string | null {
  const name = file.name.trim().toLowerCase();
  if (name.endsWith('.kml') || name.endsWith('.xml') || looksLikeKml(text)) {
    return geoJsonFromKml(text);
  }
  return normalizeAssignmentGeoJson(text);
}

export function geometryChangeToWkt(value: unknown): string | null {
  if (value == null || value === '') {
    return null;
  }
  if (typeof value === 'string') {
    return value.trim() || null;
  }
  if (typeof value === 'object') {
    const record = value as { polygonJson?: string; geometry?: string };
    const raw = record.polygonJson ?? record.geometry ?? '';
    return raw.trim() || null;
  }
  return null;
}

function geoJsonFromKml(text: string): string | null {
  const xml = new DOMParser().parseFromString(text, 'text/xml');
  if (xml.querySelector('parsererror')) {
    return null;
  }
  return normalizeAssignmentGeoJson(JSON.stringify(kml(xml)));
}

function looksLikeKml(text: string): boolean {
  return /<kml[\s>]/i.test(text);
}

function tryParseJson(value: string | null | undefined): unknown {
  if (!value?.trim()) {
    return null;
  }
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function geometryFromUnknown(value: unknown): GeoJsonObject | null {
  if (!isRecord(value)) {
    return null;
  }
  if (value.type === 'FeatureCollection') {
    const geometries = (value.features ?? [])
      .map(feature => geometryFromUnknown(feature?.geometry))
      .filter((item): item is GeoJsonObject => !!item);
    return mergeGeometries(geometries);
  }
  if (value.type === 'Feature') {
    return geometryFromUnknown(value.geometry);
  }
  if (value.type === 'GeometryCollection') {
    return mergeGeometries((value.geometries ?? [])
      .map(item => geometryFromUnknown(item))
      .filter((item): item is GeoJsonObject => !!item));
  }
  if (value.type === 'Polygon' || value.type === 'MultiPolygon') {
    return value;
  }
  return null;
}

function mergeGeometries(geometries: GeoJsonObject[]): GeoJsonObject | null {
  const polygons = geometries.flatMap(polygonRingsFromGeometry);
  if (!polygons.length) {
    return null;
  }
  if (polygons.length === 1) {
    return { type: 'Polygon', coordinates: polygons[0] };
  }
  return { type: 'MultiPolygon', coordinates: polygons };
}

function polygonRingsFromGeometry(geometry: GeoJsonObject): number[][][][] {
  if (geometry.type === 'Polygon' && Array.isArray(geometry.coordinates)) {
    return [geometry.coordinates as number[][][]];
  }
  if (geometry.type === 'MultiPolygon' && Array.isArray(geometry.coordinates)) {
    return geometry.coordinates as number[][][][];
  }
  return [];
}

function formatWktPolygon(rings: number[][][]): string {
  return `(${rings.map(ring => `(${ring.map(point => `${point[0]} ${point[1]}`).join(', ')})`).join(', ')})`;
}

function isRecord(value: unknown): value is GeoJsonObject {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}
